// React Dependencies
import React, { useEffect, useMemo, useRef, useState } from "react"
import { useLocation } from "react-router"
import { useSelector } from "react-redux"

// External Dependencies
import { useTranslation } from "react-i18next"
import { OverlayTrigger, Tooltip } from "react-bootstrap"
import SVG from "react-inlinesvg"

// Internal Dependencies
import { EmptyCard, NoteView, TableSkeleton, TableView } from "../../../../../component"
import { PRIVILEGE_ENUM } from "../../../../../constants/PrivilegeConstants.js"
import { DetailSvgIcon } from "../../../../../component/atoms/Icons/SVG"
import { formatterRowComponent } from "../../../../../component/atoms/Tabel"
import { RoutesConstants } from "../../../../../constants/RoutesConstants"
import { loadUserLogin } from "../../../../service/userManagementAction"
import { useSubheader } from "../../../../../_metronic/layout"
import { sortCaret, toAbsoluteUrl } from "../../../../../_metronic/_helpers"
import { getUrlParsingPageFilter } from "../../../../../config/EndpointCollection.js"
import { checkBadgePaymentStatus, stringFormatting, toMeter } from "../../../../helpers/TextHelper"
import { AREA_REGISTERED_ENUM, DATE_TYPE_ENUM, LABELING_CONST, isFrozen } from "../../../../../constants/StringConstant.js"
import { ICON_CONSTANTS, ILLUSTRATION_CONSTANTS } from "../../../../../constants/ImageConstants.js"
import ModalListBA from "../ModalListBA.jsx"
import { AREA } from "../../../../../constants/InitTypeConstants.js"
import { timeStampToDate } from "../../../../helpers/DateHelper.js"

export const ListCompleted = ({
  data,
  changePage,
  history,
  general,
  initialFilterTable,
  isActiveSearch,
  filterOn,
  isMultiSelect = false,
  actionType = null,
  onSelectionChange,
  initialSelectedItems = []
}) => {
  const privileges = loadUserLogin().privilege
  const location = useLocation()
  const subheader = useSubheader()
  const { t } = useTranslation()
  const { General } = useSelector((state) => state)
  const [showModalListBA, setShowModalListBA] = useState(false)
  const [selectedAreaId, setSelectedAreaId] = useState(null)
  const [selectedRows, setSelectedRows] = useState(initialSelectedItems)
  const [selectedIds, setSelectedIds] = useState(() => initialSelectedItems.map((e) => e?.land_id))

  const handleSelect = (row, isSelected) => {
    let rows = [...selectedRows]
    if (isSelected) {
      rows.push(row)
    } else {
      rows = rows.filter((e) => e?.land_id !== row?.land_id)
    }
    setSelectedRows(rows)
    setSelectedIds(rows.map((e) => e?.land_id))
    onSelectionChange?.(rows)
  }

  const handleSelectAll = (isSelect, rows) => {
    let current = [...selectedRows]
    if (isSelect) {
      rows?.forEach((row) => {
        if (!current.find((e) => e?.land_id === row?.land_id)) {
          current.push(row)
        }
      })
    } else {
      rows?.forEach((row) => {
        current = current.filter((e) => e?.land_id !== row?.land_id)
      })
    }
    setSelectedRows(current)
    setSelectedIds(current.map((e) => e?.land_id))
    onSelectionChange?.(current)
  }

  const handleOnClickReportDetail = (id) => {
    setShowModalListBA(true)
    setSelectedAreaId(id)
  }

  useEffect(() => {
    subheader.setButton(undefined)
  }, [])

  const mountedRef = useRef(false)
  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true
      return
    }
    setSelectedRows([])
    setSelectedIds([])
    onSelectionChange?.([])
  }, [isMultiSelect, actionType])

  const nonSelectableIds = useMemo(() => {
    if (!actionType || !data?.pageData) return []
    if (actionType === LABELING_CONST.FREEZE || actionType === LABELING_CONST.BLACKLIST) {
      return data.pageData
        .filter((row) => isFrozen(row.area_status) || row.area_status === LABELING_CONST.BLACKLISTED)
        .map((row) => row.land_id)
    }
    if (actionType === LABELING_CONST.UNFREEZE) {
      return data.pageData
        .filter((row) => !isFrozen(row.area_status))
        .map((row) => row.land_id)
    }
    return []
  }, [actionType, data?.pageData])

  const uiCode = (e) => {
    return (
      <>
        <div className="d-flex align-items-start mb-1">
          <p className="text-dark font-weight-bolder font-size-lg mb-0">
            {stringFormatting(e.land_name)}
          </p>
          {isFrozen(e?.area_status) && (
            <SVG src={ICON_CONSTANTS.FREEZE_STATUS} className="ml-2" />
          )}
          {e?.area_status === LABELING_CONST.BLACKLISTED && (
            <SVG src={ICON_CONSTANTS.BLACKLIST_STATUS} className="ml-2" />
          )}
        </div>
        <span className="text-muted font-weight-bold d-block">{toMeter(e?.land_area)}</span>
      </>
    )
  }
  const uiMitra = (e) => {
    return (
      <>
        <div className="d-flex align-items-start mb-1">
          <p className="text-dark font-weight-bolder font-size-lg mb-0">
            {stringFormatting(e.mitra_name)}
          </p>
          {isFrozen(e?.mitra_status) && (
            <SVG src={ICON_CONSTANTS.FREEZE_STATUS} className="ml-2" />
          )}
          {e?.mitra_status === LABELING_CONST.BLACKLISTED && (
            <SVG src={ICON_CONSTANTS.BLACKLIST_STATUS} className="ml-2" />
          )}
        </div>
        <span className="text-muted font-weight-bold d-block">
          {stringFormatting(e.mitra_phone_number)}
        </span>
      </>
    )
  }
  const uiName = (e) => {
    return (
      <>
        <div className="d-flex align-items-start mb-1">
          <p className="text-dark font-weight-bolder font-size-lg mb-0">
            {stringFormatting(e.farmer_name)}
          </p>
          {isFrozen(e?.farmer_status) && (
            <SVG src={ICON_CONSTANTS.FREEZE_STATUS} className="ml-2" />
          )}
          {e?.farmer_status === LABELING_CONST.BLACKLISTED && (
            <SVG src={ICON_CONSTANTS.BLACKLIST_STATUS} className="ml-2" />
          )}
        </div>
        <span className="text-muted font-weight-bold d-block">
          {stringFormatting(e.farmer_phone_number)}
        </span>
      </>
    )
  }
  const uiHarvestSuccess = (e) => {
    return (
      <>
        <p className="text-dark mb-1 font-size-lg">{stringFormatting(e.harvest_success_count)}</p>
      </>
    )
  }
  const uiHarvestFailed = (e) => {
    return (
      <>
        <p className="text-dark mb-1 font-size-lg">{stringFormatting(e.harvest_failed_count)}</p>
      </>
    )
  }
  const uiBACount = (e) => {
    return (
      <div
        className="d-flex align-items-center justify-content-center cursor-pointer"
        style={{ gap: 4 }}
        onClick={() => e?.ba_count > 0 ? handleOnClickReportDetail(e.land_id) : null}>
        {e?.ba_count ? (
          <SVG src={toAbsoluteUrl(ICON_CONSTANTS.NOTE_VIEW_WARNING_TRIANGEL)} />
        ) : null}
        <p className={`text-dark mb-1 font-size-lg ${e?.ba_count > 0 ? "underline" : ""}`}>
          {e?.ba_count > 0 ? e.ba_count : "-"}
        </p>
      </div>
    )
  }
  const uiBillStatus = (item) => {
    return (
      <>
        <p>{checkBadgePaymentStatus(item?.payment_status || "NON_BILL")}</p>
      </>
    )
  }

  const uiLatestConfirmDate = (e) => {
    return (
      <>
        <p className="text-dark font-weight-bolder mb-1 font-size-lg">
          {e.last_confirmation_date
            ? timeStampToDate(e.last_confirmation_date, DATE_TYPE_ENUM.ISO_DATE_STRAP)
            : "-"}
        </p>
      </>
    )
  }

  const uiAction = (e) => {
    return (
      <>
        {privileges.indexOf(PRIVILEGE_ENUM.AREA_DETAIL_REGISTERED) > -1 && (
          <OverlayTrigger overlay={<Tooltip id="products-edit-tooltip">{t("detail")}</Tooltip>}>
            <div
              className="btn btn-icon btn-light btn-hover-primary btn-sm mx-3"
              onClick={() => {
                history.push(
                  RoutesConstants.AREA.DETAIL_AREA(AREA_REGISTERED_ENUM.MT_COMPLETED, e.land_id)
                )
              }}>
              <DetailSvgIcon></DetailSvgIcon>
            </div>
          </OverlayTrigger>
        )}
      </>
    )
  }

  const columns = [
    {
      dataField: "landName",
      text: t("table_header_area_name"),
      formatter: formatterRowComponent,
      formatExtraData: {
        externalStyle: uiCode
      },
      sortCaret: sortCaret,
      sort: true,
      onSort: (field, order) => {
        let param = getUrlParsingPageFilter({
          ...initialFilterTable,
          sort: field + "," + order
        })
        history.push({ pathname: location.pathname, search: param })
      },
      headerStyle: () => {
        return { width: "17%" }
      }
    },
    {
      dataField: "mitraName,",
      text: t("table_header_mitra"),
      formatter: formatterRowComponent,
      formatExtraData: {
        externalStyle: uiMitra
      },
      sortCaret: sortCaret,
      sort: true,
      onSort: (field, order) => {
        let param = getUrlParsingPageFilter({
          ...initialFilterTable,
          sort: field + "," + order
        })
        history.push({ pathname: location.pathname, search: param })
      }
    },
    {
      dataField: "farmerName,",
      text: t("table_header_farmer"),
      formatter: formatterRowComponent,
      formatExtraData: {
        externalStyle: uiName
      },
      sortCaret: sortCaret,
      sort: true,
      onSort: (field, order) => {
        let param = getUrlParsingPageFilter({
          ...initialFilterTable,
          sort: field + "," + order
        })
        history.push({ pathname: location.pathname, search: param })
      }
    },
    {
      dataField: "harvestSuccessCount,",
      text: t("table_header_harvest_success"),
      formatter: formatterRowComponent,
      formatExtraData: {
        externalStyle: uiHarvestSuccess
      },
      sortCaret: sortCaret,
      sort: true,
      onSort: (field, order) => {
        let param = getUrlParsingPageFilter({
          ...initialFilterTable,
          sort: field + "," + order
        })
        history.push({ pathname: location.pathname, search: param })
      }
    },
    {
      dataField: "harvestFailedCount,",
      text: t("table_header_harvest_failed"),
      formatter: formatterRowComponent,
      formatExtraData: {
        externalStyle: uiHarvestFailed
      },
      sortCaret: sortCaret,
      sort: true,
      onSort: (field, order) => {
        let param = getUrlParsingPageFilter({
          ...initialFilterTable,
          sort: field + "," + order
        })
        history.push({ pathname: location.pathname, search: param })
      }
    },
    {
      dataField: "baCount",
      text: t("table_header_ba_count"),
      formatter: formatterRowComponent,
      formatExtraData: {
        externalStyle: uiBACount
      },
      sortCaret: sortCaret,
      sort: true,
      onSort: (field, order) => {
        let param = getUrlParsingPageFilter({
          ...initialFilterTable,
          sort: field + "," + order
        })
        history.push({ pathname: location.pathname, search: param })
      }
    },
    {
      dataField: "latestConfirmDate",
      text: t("table_header_last_confirm_date"),
      formatter: formatterRowComponent,
      formatExtraData: {
        externalStyle: uiLatestConfirmDate
      },
      sortCaret: sortCaret,
      sort: true,
      onSort: (field, order) => {
        let param = getUrlParsingPageFilter({
          ...initialFilterTable,
          sort: field + "," + order
        })
        history.push({ pathname: location.pathname, search: param })
      }
    },
    {
      dataField: "paymentStatus,",
      text: t("table_header_payment_status"),
      formatter: formatterRowComponent,
      formatExtraData: {
        externalStyle: uiBillStatus
      },
      sortCaret: sortCaret,
      sort: true,
      onSort: (field, order) => {
        let param = getUrlParsingPageFilter({
          ...initialFilterTable,
          sort: field + "," + order
        })
        history.push({ pathname: location.pathname, search: param })
      }
    },
    {
      dataField: "action",
      text: "ACTION",
      sort: false,
      formatter: formatterRowComponent,
      formatExtraData: {
        externalStyle: uiAction
      }
    }
  ]

  return (
    <>
      <NoteView
        variant={"success"}
        title={
          <>
            <div className="pl-6">{t("label_note_completed_mt")}</div>
          </>
        }
      />
      {(General.loading && General.actionInitType === AREA.GET_LIST_REGISTERED_V2) ? (
        <TableSkeleton column={7} row={10} />
      ) : data?.pageData.length == 0 ? (
        <EmptyCard
          subtitle={(filterOn || isActiveSearch) && t("messages_response_empty_search_subtitle")}
          image={
            filterOn || isActiveSearch
              ? ILLUSTRATION_CONSTANTS.SEARCH_NO_DATA
              : ILLUSTRATION_CONSTANTS.EMPTY_STATE_NO_DATA
          }
          body={
            filterOn || isActiveSearch
              ? t("messages_response_searching_not_found")
              : t("messages_response_empty_state", {
                  name: t("lahan")
                })
          }
        />
      ) : (
        ""
      )}

      <TableView
        key={`completed-${isMultiSelect}`}
        callbackAfterPageChange={changePage}
        columnProperties={columns}
        dataTable={data?.pageData ?? []}
        currentPage={data?.page?.currentPage ?? 1}
        currentElement={data?.page?.currentElement ?? 10}
        totalCount={data?.page?.totalCount ?? 0}
        loadingGetData={general.loading}
        actionInitType={AREA.GET_LIST_REGISTERED_V2}
        ignoreSinglePage
        rowClasses={() => {
          return "cursor-pointer"
        }}
        keyField="land_id"
        selectable={isMultiSelect}
        onSelect={handleSelect}
        onSelectAll={handleSelectAll}
        selected={selectedIds}
        nonSelectable={nonSelectableIds}
        headerColumnStyle={{ width: 40 }}
      />
      {showModalListBA && selectedAreaId && (
        <ModalListBA show={showModalListBA} setShow={setShowModalListBA} areaId={selectedAreaId} />
      )}
    </>
  )
}
